// src/components/cofounder/ActionCard.js
import React from 'react';
import { CheckCircle, XCircle, Loader, AlertTriangle, Clock } from 'lucide-react';

const statusConfig = {
  pending: { bg: 'bg-amber-50', border: 'border-amber-200', icon: AlertTriangle, iconColor: 'text-amber-500', label: 'Pending' },
  executing: { bg: 'bg-blue-50', border: 'border-blue-200', icon: Loader, iconColor: 'text-blue-500', label: 'Executing' },
  completed: { bg: 'bg-green-50', border: 'border-green-200', icon: CheckCircle, iconColor: 'text-green-500', label: 'Completed' },
  failed: { bg: 'bg-red-50', border: 'border-red-200', icon: XCircle, iconColor: 'text-red-500', label: 'Failed' },
  rejected: { bg: 'bg-gray-50', border: 'border-gray-200', icon: XCircle, iconColor: 'text-gray-400', label: 'Cancelled' }
};

const formatToolName = (name) => {
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, s => s.toUpperCase())
    .trim();
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const ActionCard = ({ action, onConfirm, onReject, compact = false }) => {
  const config = statusConfig[action.status] || statusConfig.pending;
  const StatusIcon = config.icon;

  const formatArgs = (args) => {
    if (!args) return [];
    return Object.entries(args).map(([key, value]) => ({
      key,
      value: typeof value === 'object' ? JSON.stringify(value) : String(value)
    }));
  };

  return (
    <div className={`rounded-lg border ${config.border} ${config.bg} ${compact ? 'p-2' : 'p-3'}`}>
      <div className="flex items-start gap-2">
        <StatusIcon
          size={compact ? 14 : 18}
          className={`${config.iconColor} mt-0.5 flex-shrink-0 ${action.status === 'executing' ? 'animate-spin' : ''}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`font-medium text-gray-800 truncate ${compact ? 'text-xs' : 'text-sm'}`}>
                {formatToolName(action.toolName)}
              </span>
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${config.bg} ${config.iconColor} border ${config.border}`}>
                {config.label}
              </span>
            </div>
            {action.createdAt && (
              <span className="text-[10px] text-gray-400 flex-shrink-0 flex items-center gap-0.5">
                <Clock size={10} />
                {formatTimestamp(action.createdAt)}
              </span>
            )}
          </div>

          {!compact && action.args && Object.keys(action.args).length > 0 && (
            <div className="mt-1.5 space-y-0.5">
              {formatArgs(action.args).map(({ key, value }) => (
                <div key={key} className="flex gap-2 text-xs">
                  <span className="text-gray-500 font-medium">{key}:</span>
                  <span className="text-gray-700 truncate">{value}</span>
                </div>
              ))}
            </div>
          )}

          {action.status === 'pending' && onConfirm && onReject && (
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => onConfirm(action.id)}
                className="px-3 py-1 bg-[#190192] text-white text-xs font-medium rounded-lg hover:bg-[#0f0075] transition-colors"
              >
                Confirm
              </button>
              <button
                onClick={() => onReject(action.id)}
                className="px-3 py-1 bg-white text-gray-700 text-xs font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}

          {action.status === 'failed' && action.error && (
            <p className="text-xs text-red-600 mt-1">Error: {action.error}</p>
          )}

          {action.status === 'completed' && action.result && (
            <p className="text-xs text-green-700 mt-1 truncate">
              {typeof action.result === 'string' ? action.result : JSON.stringify(action.result)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActionCard;
